<template>
  <Modal title="Revise reachouts" mode="modal" :maxWidth="560" @close="$emit('close')">
    <!-- Pre-submission: configure the revision -->
    <div v-if="!runId" class="revisionForm">
      <p class="revisionCount">
        Revising {{ reachoutIds.length }} reachout{{ reachoutIds.length > 1 ? 's' : '' }}
      </p>

      <label class="fieldLabel">Depth</label>
      <div class="depthRadios">
        <label class="depthOption" :class="{ active: depth === 'fast' }">
          <input type="radio" v-model="depth" value="fast" />
          <div>
            <strong>Fast</strong>
            <span class="depthDesc">Style &amp; tone fixes. No account research.</span>
          </div>
        </label>
        <label class="depthOption" :class="{ active: depth === 'deep' }">
          <input type="radio" v-model="depth" value="deep" />
          <div>
            <strong>Deep Research</strong>
            <span class="depthDesc">Full account research + web search. Slowest.</span>
          </div>
        </label>
      </div>

      <label class="fieldLabel">Instructions</label>
      <textarea
        v-model="instructions"
        class="input instructionsInput"
        rows="4"
        placeholder="e.g. Make these shorter and more direct. Lead with the pain point."
      />

      <div v-if="error" class="error">{{ error }}</div>
    </div>

    <!-- Post-submission: show progress -->
    <div v-else class="revisionProgress">
      <p class="progressTitle">
        {{ allDone ? 'Revision complete' : `Revising ${revisionRows.length} reachouts...` }}
      </p>

      <p v-if="skippedCount > 0" class="skippedNote">
        Skipped {{ skippedCount }} reachout{{ skippedCount > 1 ? 's' : '' }} that {{ skippedCount > 1 ? 'are' : 'is' }} not eligible for revision.
      </p>

      <!-- Small selection (≤3): per-row progress -->
      <template v-if="revisionRows.length <= 3">
        <div v-for="rev in revisionRows" :key="rev.id" class="progressRow">
          <AppIcon v-if="rev.status === 'completed'" name="checkmark" :size="16" style="color: var(--success);" />
          <AppIcon v-else-if="rev.status === 'error' || rev.status === 'aborted'" name="error" :size="16" style="color: var(--danger);" />
          <span v-else class="spinner" />
          <span class="progressLabel">{{ rev.reachout_to || rev.reachout_id }}</span>
          <span class="progressStatus">{{ rev.status }}</span>
        </div>
      </template>

      <!-- Large selection (>3): summary with progress bar -->
      <template v-else>
        <div class="progressSummary">
          <div class="progressBar">
            <div class="progressBarFill" :style="{ width: `${progressPercent}%` }" />
          </div>
          <span class="progressCounts">
            {{ doneCount }} of {{ revisionRows.length }} complete
            <template v-if="errorCount > 0"> · {{ errorCount }} error{{ errorCount > 1 ? 's' : '' }}</template>
          </span>
        </div>
      </template>

      <!-- Inline diff review for small selections -->
      <template v-if="allDone && completedRevisions.length > 0 && completedRevisions.length <= 3">
        <div v-for="rev in completedRevisions" :key="rev.id" class="revisionResult">
          <div v-if="rev.previous_subject !== rev.revised_subject" class="subjectChange">
            <strong>Subject:</strong> {{ rev.previous_subject }} → {{ rev.revised_subject }}
          </div>
          <LineDiff
            :before="rev.previous_body || ''"
            :after="rev.revised_body || ''"
            leftLabel="Original"
            rightLabel="Revised"
          />
          <div class="revisionActions">
            <button class="btn" :disabled="actionLoading" @click="revertOne(rev)">Revert</button>
          </div>
        </div>
      </template>

      <!-- Large selection done message -->
      <p v-if="allDone && revisionRows.length > 3" class="bulkDoneMsg">
        Revised reachouts are back in your pending queue. Click into any reachout to see the diff and revert if needed.
      </p>
    </div>

    <template #footer>
      <template v-if="!runId">
        <button class="btn" @click="$emit('close')">Cancel</button>
        <button
          class="btn primary"
          :disabled="!instructions.trim() || submitting"
          @click="startRevision"
        >
          {{ submitting ? 'Starting...' : 'Start Revision' }}
        </button>
      </template>
      <template v-else>
        <button class="btn" @click="$emit('close')">{{ allDone ? 'Done' : 'Close' }}</button>
      </template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Modal from './Modal.vue'
import AppIcon from './AppIcon.vue'
import LineDiff from './LineDiff.vue'
import { apiFetch } from '../services/api'

const props = defineProps<{
  reachoutIds: string[]
}>()

const emit = defineEmits<{
  close: []
  revised: []
}>()

const depth = ref('fast')
const instructions = ref('')
const submitting = ref(false)
const error = ref('')
const actionLoading = ref(false)

const runId = ref<string | null>(null)
const skippedCount = ref(0)

type RevisionRow = {
  id: string
  reachout_id: string
  reachout_to: string | null
  status: string
  previous_subject: string
  previous_body: string
  revised_subject: string | null
  revised_body: string | null
  error: string | null
}

const revisionRows = ref<RevisionRow[]>([])

const allDone = computed(() => {
  if (!runId.value || revisionRows.value.length === 0) return false
  return revisionRows.value.every(r => r.status !== 'pending')
})

const doneCount = computed(() =>
  revisionRows.value.filter(r => r.status !== 'pending').length
)

const errorCount = computed(() =>
  revisionRows.value.filter(r => r.status === 'error' || r.status === 'aborted').length
)

const progressPercent = computed(() => {
  if (revisionRows.value.length === 0) return 0
  return Math.round((doneCount.value / revisionRows.value.length) * 100)
})

const completedRevisions = computed(() =>
  revisionRows.value.filter(r => r.status === 'completed')
)

async function startRevision() {
  submitting.value = true
  error.value = ''
  try {
    const resp = await apiFetch('/revisions', {
      method: 'POST',
      body: {
        reachout_ids: props.reachoutIds,
        depth: depth.value,
        instructions: instructions.value,
        apply_learned_rules: true,
      },
    })
    skippedCount.value = resp.skipped_count || 0
    runId.value = resp.run_id
    startPolling()
  } catch (e: any) {
    error.value = e?.message || 'Failed to start revision'
  } finally {
    submitting.value = false
  }
}

let pollTimer: ReturnType<typeof setInterval> | null = null

function startPolling() {
  stopPolling()
  pollTimer = setInterval(pollStatus, 2000)
  pollStatus()
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

async function pollStatus() {
  if (!runId.value) return
  try {
    const resp = await apiFetch(`/revisions/${runId.value}`)
    revisionRows.value = (resp.revisions || []).map((r: any) => ({
      id: r.id,
      reachout_id: r.reachout_id,
      reachout_to: r.reachout_to,
      status: r.status,
      previous_subject: r.previous_subject,
      previous_body: r.previous_body,
      revised_subject: r.revised_subject,
      revised_body: r.revised_body,
      error: r.error,
    }))
    if (allDone.value) {
      stopPolling()
      emit('revised')
    }
  } catch {
    // silent
  }
}

async function revertOne(rev: RevisionRow) {
  actionLoading.value = true
  try {
    await apiFetch(`/reachouts/${rev.reachout_id}/revisions/${rev.id}/revert`, {
      method: 'POST',
    })
    rev.status = 'reverted'
    emit('revised')
  } catch {
    // silent
  } finally {
    actionLoading.value = false
  }
}

onBeforeUnmount(() => stopPolling())
</script>

<style scoped>
.revisionForm {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
}

.revisionCount {
  font-size: 14px;
  color: var(--text);
  margin: 0;
}

.fieldLabel {
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: -4px;
}

.depthRadios {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.depthOption {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}

.depthOption.active {
  border-color: var(--primary);
  background: var(--selection-bg);
}

.depthOption input[type="radio"] {
  margin-top: 3px;
}

.depthOption strong {
  display: block;
}

.depthDesc {
  display: block;
  font-size: 12px;
  color: var(--muted);
}

.instructionsInput {
  resize: vertical;
  font-size: 13px;
}

.error {
  color: var(--danger);
  font-size: 13px;
}

.skippedNote {
  font-size: 12px;
  color: var(--muted);
  margin: 0;
}

.revisionProgress {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
}

.progressTitle {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}

.progressRow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.progressLabel {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progressStatus {
  color: var(--muted);
  font-size: 12px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.progressSummary {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.progressBar {
  height: 6px;
  background: var(--border);
  border-radius: 3px;
  overflow: hidden;
}

.progressBarFill {
  height: 100%;
  background: var(--primary);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progressCounts {
  font-size: 13px;
  color: var(--muted);
}

.bulkDoneMsg {
  font-size: 13px;
  color: var(--muted);
  margin: 4px 0 0;
}

.revisionResult {
  margin-top: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px;
}

.subjectChange {
  font-size: 13px;
  margin-bottom: 8px;
}

.revisionActions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  justify-content: flex-end;
}
</style>
